import {
  endOfPriorYear,
  getMostRecentDate,
  isValidInputDate,
  minOfDates,
  startOfPriorYear,
  startOfYear,
  TZ_DEFAULT,
  xMonthsAgo,
  yyyymmdd
} from 'services/Dates';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import {
  AccountTypeOptions,
  ALL_LABEL,
  BROKERAGE_LABEL,
  COLLEGE_SAVINGS_LABEL,
  getAccountTypeForLabel,
  LAST_12_MONTHS,
  LAST_3_MONTHS,
  LAST_6_MONTHS,
  MUTUAL_FUND_LABEL,
  PRIOR_YEAR,
  SINCE_INCEPTION,
  YEAR_TO_DATE
} from 'services/Filters';

export const formatDateRange = (dateRangeFilter, balanceBarAsOfDate) => {
  const toDate =
    (!balanceBarAsOfDate && dateRangeFilter.toDate) ||
    (dateRangeFilter.toDate &&
      balanceBarAsOfDate &&
      new Date(dateRangeFilter.toDate) <= new Date(balanceBarAsOfDate))
      ? dateRangeFilter.toDate
      : balanceBarAsOfDate;
  const fromDate = dateRangeFilter.fromDate
    ? dateRangeFilter.fromDate
    : balanceBarAsOfDate;
  return {
    fromDate: yyyymmdd(fromDate, TZ_DEFAULT),
    toDate: yyyymmdd(toDate, TZ_DEFAULT)
  };
};

export const modifyDateRange = (dateRangeFilter, portfolioAsOfDate) => {
  const toDate =
    !portfolioAsOfDate ||
    minOfDates(dateRangeFilter.toDate, portfolioAsOfDate) ===
      dateRangeFilter.toDate
      ? dateRangeFilter.toDate
      : portfolioAsOfDate;
  return {
    fromDate: yyyymmdd(dateRangeFilter.fromDate, TZ_DEFAULT),
    toDate: yyyymmdd(toDate, TZ_DEFAULT)
  };
};

export const getCustomAccountTypeForLabel = (label) => ({
  label: label,
  value: label
});

export const isCustomGroupSelection = (selection) =>
  !AccountTypeOptions.some((option) => option.label.includes(selection));

export const handleAccountTypeSelection = (state, action) => {
  const filter = cloneDeep(state);
  filter.filter = isCustomGroupSelection(action.accountTypeFilterSelection)
    ? getCustomAccountTypeForLabel(action.accountTypeFilterSelection)
    : getAccountTypeForLabel(action.accountTypeFilterSelection);

  return filter;
};

export const buildTimeRangeFromFilter = (
  inceptionDate,
  dateRangeFilter,
  balanceBarAsOfDate
) => {
  const mostRecent = getMostRecentDate(1);

  const initialRange = {
    toDate:
      (dateRangeFilter.custom && dateRangeFilter.toDate) ||
      (!balanceBarAsOfDate && dateRangeFilter.toDate) ||
      (dateRangeFilter.toDate &&
        balanceBarAsOfDate &&
        new Date(dateRangeFilter.toDate) <= new Date(balanceBarAsOfDate))
        ? dateRangeFilter.toDate
        : balanceBarAsOfDate
        ? yyyymmdd(balanceBarAsOfDate)
        : mostRecent,
    fromDate:
      (dateRangeFilter.custom && dateRangeFilter.fromDate) ||
      dateRangeFilter.fromDate
        ? dateRangeFilter.fromDate
        : '' // needs to be empty for custom date range
  };

  // If initialRange.fromDate is not null, then
  // custom is toggled true and a from date already
  // exists so no need to calculate a default one
  if (initialRange.fromDate && dateRangeFilter.custom) {
    return initialRange;
  }

  switch (dateRangeFilter.period) {
    case LAST_3_MONTHS:
      return {
        ...initialRange,
        fromDate: xMonthsAgo(getMostRecentDate(1), 3)
      };
    case LAST_6_MONTHS:
      return {
        ...initialRange,
        fromDate: xMonthsAgo(getMostRecentDate(1), 6)
      };
    case LAST_12_MONTHS:
      return {
        ...initialRange,
        fromDate: xMonthsAgo(getMostRecentDate(1), 12)
      };
    case YEAR_TO_DATE:
      return {
        ...initialRange,
        fromDate: startOfYear()
      };
    case PRIOR_YEAR:
      return {
        toDate: endOfPriorYear(),
        fromDate: startOfPriorYear()
      };
    case SINCE_INCEPTION:
      return {
        fromDate: inceptionDate
          ? yyyymmdd(inceptionDate)
          : yyyymmdd(balanceBarAsOfDate),
        toDate: balanceBarAsOfDate || mostRecent
      };
    default:
      return {
        fromDate: balanceBarAsOfDate || mostRecent,
        toDate: balanceBarAsOfDate || mostRecent
      };
  }
};

export const createTimeRangeFromFilter = (
  inceptionDate,
  dateRangeFilter,
  portfolioAsOfDate
) => {
  const mostRecent = getMostRecentDate(1);

  const initialRange = {
    toDate:
      dateRangeFilter.custom &&
      dateRangeFilter.toDate &&
      (!portfolioAsOfDate ||
        minOfDates(dateRangeFilter.toDate, portfolioAsOfDate) ===
          dateRangeFilter.toDate)
        ? dateRangeFilter.toDate
        : yyyymmdd(portfolioAsOfDate || mostRecent),
    fromDate:
      dateRangeFilter.custom && dateRangeFilter.fromDate
        ? dateRangeFilter.fromDate
        : null
  };

  // If initialRange.fromDate is not null, then
  // custom is toggled true and a from date already
  // exists so no need to calculate a default one
  if (initialRange.fromDate) {
    return initialRange;
  }

  switch (dateRangeFilter.period) {
    case LAST_3_MONTHS:
      return {
        ...initialRange,
        fromDate: xMonthsAgo(getMostRecentDate(1), 3)
      };
    case LAST_6_MONTHS:
      return {
        ...initialRange,
        fromDate: xMonthsAgo(getMostRecentDate(1), 6)
      };
    case LAST_12_MONTHS:
      return {
        ...initialRange,
        fromDate: xMonthsAgo(getMostRecentDate(1), 12)
      };
    case YEAR_TO_DATE:
      return {
        ...initialRange,
        fromDate: startOfYear()
      };
    case PRIOR_YEAR:
      return {
        toDate: endOfPriorYear(),
        fromDate: startOfPriorYear()
      };
    case SINCE_INCEPTION:
      return {
        fromDate: inceptionDate
          ? yyyymmdd(inceptionDate)
          : yyyymmdd(portfolioAsOfDate),
        toDate: yyyymmdd(portfolioAsOfDate)
      };
    default:
      return {
        fromDate: yyyymmdd(portfolioAsOfDate),
        toDate: yyyymmdd(portfolioAsOfDate)
      };
  }
};

export const handleDateRangeReset = (state, defaultDateRangeFilter) => {
  const filter = cloneDeep(state);
  filter.dateRangeFilter = defaultDateRangeFilter;

  return filter;
};

export const handlePeriodChange = (state, action) => {
  const genericFilter = cloneDeep(state);
  genericFilter.dateRangeFilter.period = action.period;

  return genericFilter;
};

export const handleCustomToDateChange = (state, action) => {
  const genericFilter = cloneDeep(state);

  genericFilter.dateRangeFilter.toDate = moment(action.toDate);
  return genericFilter;
};

export const handleCustomFromDateChange = (state, action) => {
  const genericFilter = cloneDeep(state);

  genericFilter.dateRangeFilter.fromDate = moment(action.fromDate);
  return genericFilter;
};

export const toggleCustomDateFilter = (state, action) => {
  const genericFilter = cloneDeep(state);
  genericFilter.dateRangeFilter.custom = action.custom;
  genericFilter.dateRangeFilter.fromDate = '';
  genericFilter.dateRangeFilter.toDate = '';

  return genericFilter;
};

export const applyPerformanceFilters = (
  performancePage,
  performanceFilter,
  accounts
) => {
  const { accountIdsByType } = performancePage;
  const { filter } = performanceFilter;
  const preFilterAccountIds = accountIdsByType[filter.label] || [];

  return accounts.filter(
    (acct) =>
      preFilterAccountIds.includes(acct.accountId) &&
      acct.inceptionDate !== null
  );
};

export const checkIsAggregateRateOfReturnEligible = (accountIdsByType) => {
  const num529s = accountIdsByType[COLLEGE_SAVINGS_LABEL]?.length || 0;
  const numAccounts = accountIdsByType[ALL_LABEL]?.length || 0;
  const numBrokerage = accountIdsByType[BROKERAGE_LABEL]?.length || 0;
  const numMutualFunds = accountIdsByType[MUTUAL_FUND_LABEL]?.length || 0;

  const has529s = num529s > 0;
  const hasBrokerage = numBrokerage > 0;
  const hasMutualFunds = numMutualFunds > 0;
  const hasOnlyMutualFundsAnd529s =
    has529s && hasMutualFunds
      ? numMutualFunds + num529s === numAccounts
      : false;
  const hasOnlyBrokerageMutualFundsAnd529s =
    has529s && hasMutualFunds && hasBrokerage
      ? numBrokerage + numMutualFunds + num529s === numAccounts
      : false;

  return (
    has529s &&
    hasMutualFunds &&
    (hasOnlyMutualFundsAnd529s ||
      (hasBrokerage && hasOnlyBrokerageMutualFundsAnd529s))
  );
};

export const isValidPerformanceConstraint = (accounts, fromDate, toDate) => {
  const validAccounts = accounts && accounts.length > 0;
  const validFromDate = fromDate && isValidInputDate(fromDate);
  const validToDate = toDate && isValidInputDate(toDate);
  const validDateRange = new Date(fromDate) <= new Date(toDate);

  return validAccounts && validFromDate && validToDate && validDateRange;
};
